<!-- SupportEmailModal.vue -->
<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import CustomTextarea from "@/components/Generics/CustomTextarea.vue";
import { useDraggableModal } from "@/composables/useDraggableModal";

const emit = defineEmits(["close", "submit"]);

// 🔹 NOVO: arrastar o modal pelo cabeçalho (posição não persiste entre aberturas)
const { panelRef, panelStyle, isDragging, isDraggable, onHandlePointerDown } =
  useDraggableModal();

const type = ref("support"); // 'support' | 'suggestion'
const subject = ref("");
const message = ref(""); // corpo da mensagem
const isSending = ref(false);
const errorMessage = ref("");

// fecha modal
function closeModal() {
  emit("close");
}

// esc pra fechar
function handleEscKey(e) {
  if (e.key === "Escape") {
    closeModal();
  }
}

onMounted(() => {
  document.addEventListener("keydown", handleEscKey);
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleEscKey);
});

// alternar tipo de mensagem
function selectType(newType) {
  type.value = newType;
}

// enviar (por enquanto só valida e emite evento)
async function handleSubmit() {
  errorMessage.value = "";

  if (!subject.value.trim()) {
    errorMessage.value = "Informe um assunto antes de enviar.";
    return;
  }

  if (!message.value.trim()) {
    errorMessage.value = "Informe a mensagem antes de enviar.";
    return;
  }

  try {
    isSending.value = true;

    emit("submit", {
      type: type.value,
      subject: subject.value.trim(),
      message: message.value.trim(),
    });

    closeModal();
  } catch (err) {
    console.error("Erro ao enviar e-mail de suporte:", err);
    errorMessage.value =
      "Ocorreu um erro ao enviar sua mensagem. Tente novamente.";
  } finally {
    isSending.value = false;
  }
}
</script>

<template>
  <div class="modal-overlay">
    <div ref="panelRef" class="modal-panel" :style="panelStyle">
      <!-- Cabeçalho -->
      <header
        class="modal-header"
        :class="{
 'modal-header--draggable': isDraggable,
          'modal-header--dragging': isDragging,
        }"
        @pointerdown="onHandlePointerDown"
      >
        <!-- ── PATCH: ícone à esquerda do título ── -->
        <h3 class="card-title">
          <i class="fas fa-headset card-title__icon"></i>
          Fale conosco
        </h3>
        <!-- ── FIM PATCH ───────────────────────── -->
        <button
          @click="closeModal"
          type="button"
          class="modal-close-btn"
          aria-label="Fechar"
        >
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            ></path>
          </svg>
        </button>
      </header>

      <!-- Corpo -->
      <div class="panel-body">
        <div class="content-area">
          <!-- Botões de seleção: Fale conosco -->
          <div class="tab-buttons-wrapper">
            <div class="tab-buttons">
              <button
                type="button"
                class="tab-button"
                :class="{ active: type === 'support' }"
                @click="selectType('support')"
              >
                <i class="fas fa-life-ring mr-1.5"></i>
                Suporte
              </button>

              <button
                type="button"
                class="tab-button"
                :class="{ active: type === 'suggestion' }"
                @click="selectType('suggestion')"
              >
                <i class="fas fa-lightbulb mr-1.5"></i>
                Sugestão
              </button>
            </div>
          </div>

          <!-- Campo de assunto + mensagem -->
          <section class="form-section">
            <!-- Assunto com mesmo estilo de AddAuthorForm.vue -->
            <div class="form-group">
              <label class="form-label" for="support-subject">
                Assunto
              </label>
              <input
                id="support-subject"
                type="text"
                v-model="subject"
                class="form-input"
                placeholder="Descreva rapidamente o assunto da sua mensagem"
              />
            </div>

            <!-- Mensagem usando CustomTextarea -->
            <div class="form-group">
              <label class="form-label mt-3" for="support-message">
                Mensagem
              </label>
              <div id="support-message">
                <CustomTextarea
                  v-model:body="message"
                  placeholder="Descreva com mais detalhes sua dúvida ou sugestão"
                  :is-disabled="isSending"
                  style="min-height: 10rem"
                />
              </div>

              <p v-if="errorMessage" class="field-error">
                {{ errorMessage }}
              </p>

              <p class="field-hint">
                Este canal é específico para tirar dúvidas sobre o Litterae ou
                enviar sugestões de melhorias.
              </p>
            </div>
          </section>
        </div>
      </div>

      <!-- Rodapé -->
      <div class="modal-footer">
        <button
          type="button"
          class="btn btn-secondary"
          @click="closeModal"
          :disabled="isSending"
        >
          Cancelar
        </button>
        <button
          type="button"
          class="btn btn-primary"
          @click="handleSubmit"
          :disabled="isSending"
        >
          <i
            v-if="isSending"
            class="fas fa-circle-notch fa-spin mr-2"
          ></i>
          <i
            v-else
            class="fas fa-paper-plane mr-2"
          ></i>

          <span>
            {{ isSending ? "Enviando..." : "Enviar" }}
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.modal-panel {
  max-width: 560px;
}

.card-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--text-dark);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* ── PATCH: estilo do ícone no título ── */
.card-title__icon {
  font-size: 1rem;
  color: var(--text-on-brand-soft);
}

/* Body */
.panel-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.content-area {
  flex: 1;
  min-height: 0;
  padding: 0.9rem 1.25rem 1.1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

/* Botões de tipo (Suporte / Sugestão) */
.tab-buttons-wrapper {
  display: flex;
  justify-content: center;
  margin-top: 0.5rem;
}

.tab-buttons {
  display: inline-flex;
  gap: 0.25rem;
}

.tab-button {
  border: none;
  background: transparent;
  padding: 0.35rem 0.9rem;
  margin-right: 0.25rem;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--text-muted);
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out, transform 0.05s ease;
}

.tab-button:hover {
  background-color: var(--brand-tint);
}

.tab-button:focus,
.tab-button:focus-visible {
  outline: none;
  box-shadow: none;
}

/* Ativo = lilás */
.tab-button.active {
  background-color: var(--brand-soft);
  color: var(--text-on-brand-soft);
  border: none;
  transform: scale(1.07);
}

.tab-button:active {
  transform: translateY(1px);
}

/* Campo de formulário */
.form-section {
  margin-top: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.mt-3 {
  margin-top: 0.75rem;
}

.field-hint {
  font-size: 0.75rem;
  color: var(--text-muted);
  margin-top: 0.25rem;
}

.field-error {
  font-size: 0.8rem;
  color: var(--danger-text);
}

</style>